// src/helpers/mapKey.js
//
// Collision-free Map keys for items that can be docs OR folders.
// Both tables (docs, project_folders) use independent integer sequences that
// can independently produce the same numeric id (e.g. doc id=5 and folder id=5).
// Prefixing with the item type ensures they never collide in a single flat Map.

/** Canonical Map key for any item that has .type and .id */
export const itemMapKey   = (item) => `${item.type}:${item.id}`;

/** Key for a known doc by numeric id */
export const docMapKey    = (id)   => `doc:${id}`;

/** Key for a known folder by numeric id */
export const folderMapKey = (id)   => `folder:${id}`;

const isTypedMapKey = (value) =>
  typeof value === 'string' && value.includes(':');

/**
 * Look up an item from itemsMap by numeric DB id.
 *
 * - When `type` is provided: tries the typed key first.
 * - When `type` is omitted: tries doc key then folder key.
 * - When `id` is already a typed key (`doc:1`, `folder:1`), it is used directly.
 *
 * @param {Map} map        The itemsMap (Map<string,item>)
 * @param {number|string} id  The numeric DB id (or typed key, handled gracefully)
 * @param {string|null} type  'doc' | 'folder' | null
 * @returns {object|null}
 */
export const findItem = (map, id, type = null) => {
  if (id == null || !map) return null;
  if (typeof map.get !== 'function') return null;

  if (isTypedMapKey(id)) {
    return map.get(id) ?? null;
  }

  if (type === 'doc')
    return map.get(docMapKey(id)) ?? null;
  if (type === 'folder')
    return map.get(folderMapKey(id)) ?? null;
  return map.get(docMapKey(id)) ?? map.get(folderMapKey(id)) ?? null;
};
